// The running play: loads the level and the play record, keeps the board
// state with the shared core, sends intents to the Worker and applies what it
// answers. Display reads this store and dispatches the actions below.
import {
  clearAll as coreClearAll,
  clearLatest as coreClearLatest,
  createPlayState,
  precheckSubmission,
  replayMoves,
  scorePlay,
  tapNode,
  type Mode,
  type OutcomeCode,
  type PlayLevel,
  type PlayState,
  type Variant,
} from '@wits/core';
import { ApiError } from '../api/client';
import { inPlaytest, playBackend } from './backend';
import type { PlayRecord, WordResponse } from '../api/types';
import { allLevels, nightStore } from '../night/nightStore';
import { howToPlayDue, noteHowToPlayShown, optOutOfHowToPlay } from '../settings/howToPlay';
import { createStore } from '../store';
import { loadExtraWords, saveExtraWords } from './extraWords';
import { goalHint, outcomeText, type OutcomeContext } from './outcomeText';

export interface Toast {
  key: number;
  code: OutcomeCode | 'error';
  text: string;
  /** Short-lived for accepted words. */
  brief: boolean;
}

export type Confirm = { kind: 'finish'; final: number } | { kind: 'remove'; word: string };

/** The play menu's tabs: Game Info, and Completion once the play is finished. */
export type MenuTab = 'info' | 'completion';

/** The menu's expandable sections, across both tabs. */
export type MenuSection = 'how-to-play' | 'score-detail' | 'settings' | 'debug' | 'sky' | 'score' | 'stats' | 'constellations';

export interface PlaySessionState {
  mode: Mode | null;
  variant: Variant | null;
  phase: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  /** Whether Try again can help: not when the level is on no night the address can name. */
  errorRetry: boolean;
  /** The night the play was opened on: the date asked for, else tonight by the Worker's clock. */
  night: string | null;
  level: PlayLevel | null;
  play: PlayRecord | null;
  board: PlayState | null;
  busy: boolean;
  toast: Toast | null;
  confirm: Confirm | null;
  menuOpen: boolean;
  menuTab: MenuTab;
  /** The sections open in the menu; several can be open at once. */
  menuSections: MenuSection[];
  /** Puzzle: valid words spelled that were not part of the solution (extraWords.ts). */
  extraWords: string[];
  detailOpen: boolean;
  /** The How to Play card is up, over a mask, before the level begins (settings/howToPlay.ts). */
  howToPlay: boolean;
  /** The sky has finished its trip up to the play; the card and the level's beginning wait for it. */
  arrived: boolean;
  /** The level has begun: the card is gone, or was not due, and the stars take taps. */
  begun: boolean;
  /** The goal panel's shine, once, as a Puzzle begins. */
  goalShine: boolean;
  /** The instruction in the feedback spot, from the level's beginning until the first star is selected. */
  hint: string | null;
  /** The last accepted word, for its sequence on the board; each acceptance has its own seq. */
  justAccepted: { word: string; seq: number } | null;
  /** A playtest from the level designer: a local play, Home is Quit, Reset under Debug, no sharing (systems/play/playtest.ts). */
  playtest: boolean;
  /** Apogee: the planet waiting for its alignment to be chosen (docs/02-Game-Design/Alignments.md); nothing else can happen until it is. */
  alignmentPending: number | null;
  /** The picker is up: once the surrounding word's sequence has settled, or at once on a resume. */
  pickerOpen: boolean;
  /** The planet whose tooltip is open, and where its centre is on the screen (display/play/PlanetTip.tsx). */
  planetTip: { planet: number; x: number; y: number } | null;
}

const initial: PlaySessionState = {
  mode: null,
  variant: null,
  phase: 'idle',
  error: null,
  errorRetry: false,
  night: null,
  level: null,
  play: null,
  board: null,
  busy: false,
  toast: null,
  confirm: null,
  menuOpen: false,
  menuTab: 'info',
  menuSections: [],
  extraWords: [],
  detailOpen: false,
  howToPlay: false,
  arrived: false,
  begun: false,
  goalShine: false,
  hint: null,
  justAccepted: null,
  playtest: false,
  alignmentPending: null,
  pickerOpen: false,
  planetTip: null,
};

export const playSession = createStore<PlaySessionState>(initial);

let toastKey = 0;
let loadToken = 0;
let acceptSeq = 0;
// The last word of a Puzzle opens the completion once its sequence has played
// on the board (acceptSettled), or after COMPLETION_FALLBACK_MS regardless.
const COMPLETION_FALLBACK_MS = 6000;
let completionAfterSeq: number | null = null;
let completionFallback: ReturnType<typeof setTimeout> | undefined;
// The word that surrounds a planet opens the alignment picker once its
// sequence has played (acceptSettled), or after PICKER_FALLBACK_MS regardless.
const PICKER_FALLBACK_MS = 4000;
let pickerAfterSeq: number | null = null;
let pickerFallback: ReturnType<typeof setTimeout> | undefined;

function patch(p: Partial<PlaySessionState>): void {
  playSession.set((s) => ({ ...s, ...p }));
}

export function isFinished(s: PlaySessionState): boolean {
  return s.play?.status === 'finished';
}

function showToast(code: OutcomeCode | 'error', ctx: OutcomeContext = {}, brief = false): void {
  const text = outcomeText(code, ctx);
  if (!text) return;
  patch({ toast: { key: ++toastKey, code, text, brief } });
}

export function dismissToast(key: number): void {
  playSession.set((s) => (s.toast?.key === key ? { ...s, toast: null } : s));
}

/** Rebuild the board from the server's record: resume and verification share the core's replay. */
function boardFrom(level: PlayLevel, play: PlayRecord): PlayState {
  try {
    return replayMoves(level, play.moves);
  } catch {
    return createPlayState(level);
  }
}

/**
 * The board rebuilt from the record. The record keeps no word in progress, so
 * `keepSelection` carries it over, as a removal does: freeing a
 * constellation's stars never makes the word in progress illegal.
 */
function applyPlay(play: PlayRecord, keepSelection = false): void {
  playSession.set((s) => {
    if (!s.level) return s;
    const rebuilt = boardFrom(s.level, play);
    const board = keepSelection && s.board ? { ...rebuilt, selection: s.board.selection, selectionLines: s.board.selectionLines } : rebuilt;
    // A planet waiting for its alignment holds the play; the picker comes up
    // at once unless the word's sequence is still to play (pickerAfterSeq).
    const alignmentPending = play.score?.pending ?? null;
    const pickerOpen = alignmentPending !== null && (s.pickerOpen || (pickerAfterSeq === null && s.begun));
    return { ...s, play, board, alignmentPending, pickerOpen };
  });
}

/** The menu as it opens: on Completion with The Sky is Written open once the play is finished, on Game Info before. */
function menuDefaults(finished: boolean): Pick<PlaySessionState, 'menuTab' | 'menuSections'> {
  return finished ? { menuTab: 'completion', menuSections: ['sky'] } : { menuTab: 'info', menuSections: [] };
}

function openCompletion(): void {
  patch({ menuOpen: true, ...menuDefaults(true), confirm: null, detailOpen: false });
}

/** The night's entry for a level, its own or its week's, where the home has loaded the night. */
function nightEntry(levelId: string, date?: string) {
  const night = nightStore.get().night;
  if (!night || night.date !== (date ?? night.tonight)) return null;
  return allLevels(night).find((l) => l.levelId === levelId) ?? null;
}

/** Open tonight's play on a level, or a past night's when a date is given. */
export async function openPlay(levelId: string, date?: string): Promise<void> {
  const token = ++loadToken;
  const cardDue = !inPlaytest() && howToPlayDue();
  return loadPlay(token, levelId, date, cardDue);
}

async function loadPlay(token: number, levelId: string, date: string | undefined, cardDue: boolean): Promise<void> {
  const entry = nightEntry(levelId, date);
  const playtest = inPlaytest();
  playSession.set({ ...initial, mode: entry?.mode ?? null, variant: entry?.variant ?? null, phase: 'loading', arrived: playSession.get().arrived, playtest });
  try {
    const play = await playBackend().start(levelId, date);
    const level = await playBackend().level(play.levelId, play.version);
    if (token !== loadToken) return;
    const mode = level.mode;
    const variant = level.variant;
    const board = boardFrom(level, play);
    const open = play.status === 'open';
    const card = open && cardDue;
    if (card) noteHowToPlayShown();
    playSession.set({
      ...initial,
      mode,
      variant,
      phase: 'ready',
      night: date ?? new Date(play.serverTime).toISOString().slice(0, 10),
      level,
      play,
      board,
      // Arriving at a finished play leaves the menu closed so the pan up
      // lands on the sky; it opens on Completion once the player opens it.
      extraWords: level.mode === 'puzzle' && !playtest ? loadExtraWords(play.id) : [],
      // A Puzzle opens with its goal showing.
      detailOpen: open && level.mode === 'puzzle',
      howToPlay: card,
      arrived: playSession.get().arrived,
      playtest,
      alignmentPending: play.score?.pending ?? null,
    });
    if (open && !card) beginLevel();
  } catch (err) {
    if (token !== loadToken) return;
    if (err instanceof ApiError && err.body?.error === 'no_level') {
      // Tonight's address outlived its night: the level, and any play on it, now belong to that night in the archive.
      const error = date ? "This level isn't on that night's sky." : 'That night has ended. Its levels wait in the archive: find the night in the calendar on Home.';
      return patch({ phase: 'error', error, errorRetry: false });
    }
    const message = err instanceof ApiError ? err.message : 'The sky is out of reach. Check your connection.';
    patch({ phase: 'error', error: message, errorRetry: true });
  }
}

/**
 * The level begins once the sky has arrived: the goal shines and its line
 * waits in the feedback spot for the first star.
 */
function beginLevel(): void {
  playSession.set((s) => {
    if (!s.arrived || s.begun || !s.level || !s.board) return s;
    const unfound = s.play?.puzzle?.blanks.filter((b) => b.word === null).length ?? 0;
    return {
      ...s,
      begun: true,
      goalShine: s.level.mode === 'puzzle' && s.detailOpen,
      hint: s.board.selection.length === 0 ? goalHint(s.level, unfound) : null,
      // A resumed play with a choice still to make opens the picker as it begins.
      pickerOpen: s.alignmentPending !== null,
    };
  });
}

/** The scene host has finished the trip up to the play: the card may show, or the level begin. */
export function skyArrived(): void {
  if (playSession.get().arrived) return;
  patch({ arrived: true });
  const s = playSession.get();
  if (s.phase === 'ready' && s.play?.status === 'open' && !s.howToPlay) beginLevel();
}

/** Understood: the card and its mask go and the level begins. */
export function dismissHowToPlay(dontShowAgain: boolean): void {
  if (!playSession.get().howToPlay) return;
  if (dontShowAgain) optOutOfHowToPlay();
  patch({ howToPlay: false });
  beginLevel();
}

export function endGoalShine(): void {
  patch({ goalShine: false });
}

export function closePlay(): void {
  loadToken++;
  completionAfterSeq = null;
  clearTimeout(completionFallback);
  pickerAfterSeq = null;
  clearTimeout(pickerFallback);
  playSession.set(initial);
}

/** The board has played (or skipped) the accepted word's sequence. */
export function acceptSettled(seq: number): void {
  if (pickerAfterSeq === seq) {
    pickerAfterSeq = null;
    clearTimeout(pickerFallback);
    playSession.set((s) => (s.alignmentPending !== null ? { ...s, pickerOpen: true } : s));
  }
  if (completionAfterSeq !== seq) return;
  completionAfterSeq = null;
  clearTimeout(completionFallback);
  openCompletion();
}

function interactive(s: PlaySessionState): s is PlaySessionState & { board: PlayState; level: PlayLevel; play: PlayRecord } {
  return s.phase === 'ready' && s.begun && !!s.board && !!s.level && !!s.play && !s.busy && !isFinished(s) && !s.confirm && s.alignmentPending === null;
}

/** The alignment chosen for the planet waiting on one: recorded by the Worker, then the play goes on. */
export async function chooseAlignment(alignment: string): Promise<void> {
  const s = playSession.get();
  if (s.phase !== 'ready' || !s.play || s.busy || s.alignmentPending === null || isFinished(s)) return;
  patch({ busy: true });
  try {
    const res = await playBackend().align(s.play.id, s.alignmentPending, alignment);
    applyPlay(res.play);
  } catch (err) {
    handleApiError(err);
  } finally {
    patch({ busy: false });
  }
}

export function tap(id: number): void {
  const s = playSession.get();
  if (!interactive(s)) return;
  const outcome = tapNode(s.board, id);
  if (outcome.code === 'refused_locked') {
    // Tapping a constellation asks to remove it where the variant allows it.
    if (s.board.selection.length === 0 && outcome.word) return askRemove(outcome.word);
    return showToast('refused_locked', { word: outcome.word });
  }
  if (outcome.code === 'refused_blocked' || outcome.code === 'refused_no_line') return showToast(outcome.code);
  // The first star selected takes the instruction away for good.
  patch({ board: outcome.state, toast: null, hint: null });
}

/** A tap on a planet: its tooltip, at the planet; a tap elsewhere closes it (the tooltip listens). */
export function openPlanetTip(planet: number, x: number, y: number): void {
  const s = playSession.get();
  if (s.phase !== 'ready' || !s.board || !s.begun) return;
  patch({ planetTip: s.planetTip?.planet === planet ? null : { planet, x, y } });
}

export function closePlanetTip(): void {
  if (playSession.get().planetTip) patch({ planetTip: null });
}

export function clearLatest(): void {
  const s = playSession.get();
  if (!interactive(s)) return;
  patch({ board: coreClearLatest(s.board).state });
}

export function clearAll(): void {
  const s = playSession.get();
  if (!interactive(s)) return;
  patch({ board: coreClearAll(s.board).state });
}

export async function submit(): Promise<void> {
  const s = playSession.get();
  if (!interactive(s) || s.board.selection.length === 0) return;
  const pre = precheckSubmission(s.board);
  if (pre) {
    // A refused word clears the selection; the toast names it.
    patch({ board: coreClearAll(s.board).state });
    return showToast(pre.code, { word: pre.word, n: pre.minWordLength });
  }
  patch({ busy: true });
  try {
    const res: WordResponse = await playBackend().word(s.play.id, s.board.selection);
    handleWordResponse(res);
  } catch (err) {
    handleApiError(err);
  } finally {
    patch({ busy: false });
  }
}

function handleWordResponse(res: WordResponse): void {
  const s = playSession.get();
  const level = s.level!;
  switch (res.outcome) {
    case 'accepted': {
      // A word that surrounds a planet with a choice to make opens the picker once its sequence has played.
      const seq = ++acceptSeq;
      if (res.play.score?.pending !== null && res.play.score?.pending !== undefined) {
        pickerAfterSeq = seq;
        clearTimeout(pickerFallback);
        pickerFallback = setTimeout(() => acceptSettled(seq), PICKER_FALLBACK_MS);
      }
      applyPlay(res.play);
      const ctx: OutcomeContext = {};
      if (level.mode === 'apogee' && res.score) ctx.points = res.score.total;
      if (res.word) patch({ justAccepted: { word: res.word, seq } });
      if (level.mode === 'apogee') showToast('accepted', ctx, true);
      else patch({ toast: null });
      break;
    }
    case 'accepted_level_complete': {
      applyPlay(res.play);
      showToast('accepted_level_complete');
      if (!res.word) return openCompletion();
      const seq = ++acceptSeq;
      completionAfterSeq = seq;
      clearTimeout(completionFallback);
      completionFallback = setTimeout(() => acceptSettled(seq), COMPLETION_FALLBACK_MS);
      patch({ justAccepted: { word: res.word, seq } });
      break;
    }
    default:
      // A refused word clears the selection (the record has none); the toast names it.
      applyPlay(res.play);
      if (res.outcome === 'not_in_solution' && res.word) noteExtraWord(res.word);
      showToast(res.outcome, { word: res.word ?? undefined, n: res.minWordLength });
  }
}

function noteExtraWord(word: string): void {
  const s = playSession.get();
  if (!s.play || s.extraWords.includes(word)) return;
  const extraWords = [...s.extraWords, word];
  if (!s.playtest) saveExtraWords(s.play.id, extraWords);
  patch({ extraWords });
}

function handleApiError(err: unknown): void {
  if (err instanceof ApiError && err.body) {
    const body = err.body;
    if (body.play) applyPlay(body.play);
    const code = (body.outcome ?? body.error) as OutcomeCode;
    // A refused removal or alignment means the board had drifted from the
    // record; the record just applied is the whole answer, so nothing is said.
    if (code === 'remove_refused' || code === 'align_refused') {
      if (!body.play) void reload();
      return;
    }
    const text = outcomeText(code, { word: body.word });
    if (text) return showToast(code, { word: body.word });
    if (body.error === 'play_finished') {
      void reload();
      return;
    }
    return showToast('error');
  }
  showToast('error');
}

/** Refresh the record from the server, for instance after a conflict. */
export async function reload(): Promise<void> {
  const s = playSession.get();
  if (!s.play) return;
  try {
    const play = await playBackend().get(s.play.id);
    applyPlay(play);
    if (play.status === 'finished') openCompletion();
  } catch {
    showToast('error');
  }
}

export function askRemove(word: string): void {
  const s = playSession.get();
  if (!interactive(s)) return;
  if (!s.board.constellations.some((c) => c.word === word)) return;
  patch({ confirm: { kind: 'remove', word } });
}

export function askFinish(): void {
  const s = playSession.get();
  if (!interactive(s) || s.level.mode !== 'apogee') return;
  patch({ confirm: { kind: 'finish', final: scorePlay(s.level, s.board).running } });
}

export function cancelConfirm(): void {
  patch({ confirm: null });
}

export async function confirmAction(): Promise<void> {
  const s = playSession.get();
  const confirm = s.confirm;
  if (!confirm || !s.play || s.busy) return;
  patch({ busy: true, confirm: null });
  try {
    if (confirm.kind === 'remove') {
      const res = await playBackend().remove(s.play.id, confirm.word);
      applyPlay(res.play, true);
    } else {
      const res = await playBackend().finish(s.play.id);
      applyPlay(res.play);
      showToast(res.outcome);
      openCompletion();
    }
  } catch (err) {
    handleApiError(err);
  } finally {
    patch({ busy: false });
  }
}

/** Opening picks the tab and sections afresh; closing leaves them, so the tray slides out unchanged. */
export function setMenuOpen(open: boolean): void {
  playSession.set((s) => (open ? { ...s, menuOpen: true, ...menuDefaults(isFinished(s)) } : { ...s, menuOpen: false }));
}

/** Completion is there only once the play is finished. */
export function selectMenuTab(tab: MenuTab): void {
  playSession.set((s) => (tab === 'completion' && !isFinished(s) ? s : { ...s, menuTab: tab }));
}

export function toggleMenuSection(section: MenuSection): void {
  playSession.set((s) => ({
    ...s,
    menuSections: s.menuSections.includes(section) ? s.menuSections.filter((x) => x !== section) : [...s.menuSections, section],
  }));
}

export function toggleDetail(): void {
  playSession.set((s) => ({ ...s, detailOpen: !s.detailOpen, goalShine: false }));
}

export function closeDetail(): void {
  patch({ detailOpen: false, goalShine: false });
}
